import axios from 'axios';
import queryString from 'query-string';
import React, { Fragment, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteWaveModal from './DeleteWaveModal';
import useAuth from '../hooks/useAuth';
import WaveQRCode from './WaveQRCode';
import * as XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

function Waves() {
  // State principali - solo i dati reali, non derivati
  const [allWaves, setAllWaves] = useState([]);
  const [waveData, setWaveData] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  
  // State per filtri - non duplichiamo i dati
  const [searchByStatus, setSearchByStatus] = useState("");
  const [searchByWaveId, setSearchByWaveId] = useState("");
  const [activeTab, setActiveTab] = useState('open'); // 'open' o 'closed'
  
  // State per UI
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [visibleWaveId, setVisibleWaveId] = useState(null);
  const [downloadPDF, setDownloadPDF] = useState(null);
  const [aWaveDeleteIds, setAWaveDeleteIds] = useState([]);
  
  const isLoadingRef = useRef(false); // Ref per evitare chiamate multiple

  const { waveType } = useParams();
  const navigate = useNavigate();
  const { user, signout } = useAuth();

  // Computed value: waves filtrate derivate dai dati, non duplicate nello state
  const waves = useMemo(() => {
    if (!allWaves || allWaves.length === 0) return [];
    
    let filtered = allWaves;
    
    // Filtra per tab (open/closed)
    if (activeTab === 'open') {
      filtered = filtered.filter(w => w.status === 'default' || w.status === 'progress');
    } else {
      filtered = filtered.filter(w => w.status === 'completed' || w.status === 'anomaly');
    }
    
    // Filtra per status se selezionato
    if (searchByStatus && searchByStatus !== '') {
      filtered = filtered.filter(item => item.status === searchByStatus);
    }
    
    // Filtra per wave ID se inserito - ricerca più flessibile
    if (searchByWaveId && searchByWaveId !== '') {
      filtered = filtered.filter(item => 
        String(item.waveid).toLowerCase().includes(String(searchByWaveId).toLowerCase())
      );
    }
    
    return filtered;
  }, [allWaves, activeTab, searchByStatus, searchByWaveId]);

  const logout = useCallback(() => {
    signout(() => {
      navigate('/');
    });
  }, [signout, navigate]);

  function getOrderStatus(pickedQty, sourceTargetQty, user) {
    if (user) {
      return ((sourceTargetQty - pickedQty) === 0) ? "completed" : "uncompleted";
    }
    else
      return "untaken"

  }

  function getOrderColor(pickedQty, sourceTargetQty, user) {
    const status = getOrderStatus(pickedQty, sourceTargetQty, user)
    switch (status) {
      case "completed":
        return "lightgray";
      case "uncompleted":
        return "#FAA99B";
      default:
        return "none"
    }
  }

  const getWaves = useCallback(async function getWaves() {
    // Evita chiamate multiple simultanee
    if (isLoadingRef.current) {
      return;
    }
    
    isLoadingRef.current = true;
    setLoading(true);
    setLoadingProgress({ current: 0, total: 0 });
    const qs = queryString.stringify({ 'company': user.company, 'wavetype': waveType });
    try {
      const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_API_PREFIX}/bonzai/waves/getWaves?${qs}`,
        headers: { Authorization: `Bearer ${user.token}` },
        timeout: 15000 // Timeout aumentato
      });
      if (response && response.data && response.data.result) {
        
        // FASE 1: Carica subito la lista base delle wave per mostrare l'UI
        const baseWaves = response.data.result.map(item => {
          const status = (item.value.ordersTaken === 0) ? 'default' : 
                        item.value.percentage === 100 ? 'completed' : 
                        (item.value.ordersTaken < item.value.ordersCount ? 'progress' : 'anomaly');
          
          return { ...item.value, status, orders: [], startTime: '-', endTime: '-' };
        });
        
        // Mostra subito le wave base
        setAllWaves(baseWaves);
        setLoading(false);
        
        // FASE 2: Carica i dettagli in background - SOLO per le wave in progress/completed
        const wavesToFetch = response.data.result.filter(item => {
          const status = (item.value.ordersTaken === 0) ? 'default' : 
                        item.value.percentage === 100 ? 'completed' : 
                        (item.value.ordersTaken < item.value.ordersCount ? 'progress' : 'anomaly');
          return status === 'progress' || status === 'completed' || status === 'anomaly';
        });
        
        if (wavesToFetch.length > 0) {
          // Carica dettagli in background con batch più grandi e paralleli
          const processBatchOptimized = async (waves, batchSize = 8) => {
            setLoadingProgress({ current: 0, total: waves.length });
            
            for (let i = 0; i < waves.length; i += batchSize) {
              const batch = waves.slice(i, i + batchSize);
              
              // Fetch parallelo senza delay - più veloce
              const batchResults = await Promise.allSettled(batch.map(async item => {
                const status = (item.value.ordersTaken === 0) ? 'default' : 
                              item.value.percentage === 100 ? 'completed' : 
                              (item.value.ordersTaken < item.value.ordersCount ? 'progress' : 'anomaly');
                
                // Fetch con timeout più breve per velocità
                try {
                  const qsWave = queryString.stringify({
                    company: user.company,
                    waveid: item.value.waveid,
                    productID: user.company,
                    wavetype: waveType
                  });
                  
                  const resp = await axios({
                    method: 'get',
                    url: `${process.env.REACT_APP_API_PREFIX}/bonzai/waves/getWave?${qsWave}`,
                    headers: { Authorization: `Bearer ${user.token}` },
                    timeout: 8000 // Timeout ridotto per velocità
                  });
                  
                  if (resp.data?.result?.orders) {
                    const orders = resp.data.result.orders;
                    let startTime = '-', endTime = '-';
                    
                    if (orders.length > 0) {
                      // Calcolo ottimizzato dei tempi - una sola passata
                      let minStartTime = null, maxEndTime = null;
                      for (const order of orders) {
                        if (order.startTime && order.startTime !== '-' && order.startTime !== 0) {
                          const st = Number(order.startTime);
                          if (!minStartTime || st < minStartTime) minStartTime = st;
                        }
                        if (order.endTime && order.endTime !== '-' && order.endTime !== 0) {
                          const et = Number(order.endTime);
                          if (!maxEndTime || et > maxEndTime) maxEndTime = et;
                        }
                      }
                      if (minStartTime) startTime = minStartTime;
                      if (maxEndTime) endTime = maxEndTime;
                    }
                    
                    return { ...item.value, status, orders, startTime, endTime };
                  }
                } catch (e) {
                  // In caso di errore, ritorna la wave base
                }
                
                return { ...item.value, status, orders: [], startTime: '-', endTime: '-' };
              }));
              
              // Aggiorna lo stato con i risultati batch
              const successfulResults = batchResults
                .filter(result => result.status === 'fulfilled')
                .map(result => result.value);
              
              if (successfulResults.length > 0) {
                setAllWaves(prevWaves => {
                  const updatedWaves = [...prevWaves];
                  successfulResults.forEach(newWave => {
                    const index = updatedWaves.findIndex(w => w.waveid === newWave.waveid);
                    if (index !== -1) {
                      updatedWaves[index] = newWave;
                    }
                  });
                  return updatedWaves;
                });
              }
              
              // Aggiorna progresso
              setLoadingProgress({ 
                current: Math.min(i + batchSize, waves.length), 
                total: waves.length 
              });
              
              // Nessun delay - carica al massimo della velocità
            }
            
            setLoadingProgress({ current: 0, total: 0 });
          };
          
          // Carica dettagli in background
          processBatchOptimized(wavesToFetch).catch(console.error);
        }
        
        isLoadingRef.current = false;
      } else {
        setLoading(false);
        setAllWaves([]);
        setLoadingProgress({ current: 0, total: 0 });
        isLoadingRef.current = false;
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        logout();
      }
      setLoading(false);
      setAllWaves([]);
      setLoadingProgress({ current: 0, total: 0 });
      isLoadingRef.current = false;
      console.log(error);
    }
  }, [user.company, user.token, waveType, logout]);

const getWaveInfo = useCallback((waveId) => {
  // Trova la wave nei dati già caricati invece di fare una nuova chiamata API
  const waveFound = allWaves.find(w => w.waveid === waveId);
  if (waveFound && waveFound.orders && waveFound.orders.length > 0) {
    setWaveData(waveFound.orders);
    return; // Esci dalla funzione dopo aver impostato i dati
  }
  
  // Se la wave ha già orders vuoto, evita chiamate API inutili
  //if (waveFound && waveFound.status === 'default') {
  //  setWaveData([]);
  //  return;
  //}
  
  // Fallback: chiamata API ottimizzata se non ci sono dati nella wave
  const waveObj = {
    'company': user.company,
    'waveid': waveId,
    'productID': user.company,
    'wavetype': waveType
  };
  const qs = queryString.stringify(waveObj);
  
  // Usa async/await per performance migliori
  const fetchWaveData = async () => {
    try {
      const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_API_PREFIX}/bonzai/waves/getWave?${qs}`,
        headers: { Authorization: `Bearer ${user.token}` },
        timeout: 5000 // Timeout ridotto per velocità
      });
      
      if (response.data?.result?.orders) {
        const orders = response.data.result.orders;
        setWaveData(orders);
        
        // Aggiorna anche la cache in allWaves per future visualizzazioni
        setAllWaves(prevWaves => {
          const updatedWaves = [...prevWaves];
          const index = updatedWaves.findIndex(w => w.waveid === waveId);
          if (index !== -1) {
            updatedWaves[index] = { ...updatedWaves[index], orders };
          }
          return updatedWaves;
        });
      } else {
        setWaveData([]);
      }
    } catch (error) {
      console.log('getWaveInfo - error:', error);
      if (error.response?.status === 401) {
        logout();
      } else {
        setWaveData([]);
      }
    }
  };
  
  fetchWaveData();
}, [allWaves, user.company, user.token, waveType, logout]);

  function handleWaveDeleteCheck(waveId, checked) {
    let tempIds = [...aWaveDeleteIds];

    if (!checked) {
      const index = tempIds.indexOf(waveId);
      tempIds.splice(index, 1);
      setAWaveDeleteIds([...tempIds]);
    } else {
      tempIds.push(waveId);
      setAWaveDeleteIds([...tempIds]);
    }
  }

  function handleMultipleWaveDelete() {
    if (aWaveDeleteIds.length > 0) {
      setShowDeleteModal(true);
    }
  }

  useEffect(() => {
    getWaves();
  }, [getWaves]);

  // Effetto per gestire il cambio di tab - resetta solo status filter
  useEffect(() => {
    setSearchByStatus('');
    setAWaveDeleteIds([]);
  }, [activeTab]);

  // Effetto separato per gestire la wave visibile quando cambiano i filtri
  useEffect(() => {
    if (visibleWaveId && !waves.find(w => w.waveid === visibleWaveId)) {
      setWaveData(null);
      setVisibleWaveId(null);
    }
  }, [waves, visibleWaveId]);

  useEffect(() => {
  if (downloadPDF) {
    const pdfContent = document.getElementById('pdfContent');
    if (pdfContent) {
      html2canvas(pdfContent).then(canvas => {
        const imgData = canvas.toDataURL('image/jpeg');
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'px',
          format: 'a4'
        });
        const width = pdf.internal.pageSize.getWidth();
        const height = pdf.internal.pageSize.getHeight();
        pdf.addImage(imgData, 'JPEG', 0, 50, width, height);
        pdf.save(downloadPDF + '.pdf');
        setDownloadPDF(null); // reset dopo che hai salvato
      });
    }
  }
}, [downloadPDF]);


  // Handler semplificati - ora aggiornano solo gli state dei filtri
  const handleStatusChange = (status) => {
    setSearchByStatus(status);
    setAWaveDeleteIds([]);
  };

  const handleWaveIdChange = (waveId) => {
    setSearchByWaveId(waveId);
  };

  const handleSearchSubmit = () => {
    // Non fa nulla - il filtro è automatico tramite useMemo
    // Manteniamo la funzione per compatibilità con gli event handler
  };

  // RIMOSSO: ora i dati ordini e tempi sono già dentro ogni wave

  const exportWave = (waveId) => {
    let exportData = [];

    if (waveData)
      waveData.forEach(wd => {
        exportData.push({
          "Transfer Order Number": wd.Transfer_Order_Number,
          "Creation Date": wd.Creation_Date,
          "Storage Loaction": wd.Storage_location,
          "Source Storage Type": wd.Source_Storage_Type,
          "Source Storage Bin": wd.Source_Storage_Bin,
          "Material": wd.Material,
          "Material Description": wd.Material_Description,
          "Source target qty": wd.Source_target_qty,
          "Picked qty": wd.picked_qty,
          "Dest. storage Type": wd.Dest_Storage_Type,
          "Dest. Storage Bin": wd.Dest_Storage_Bin,
          "User": wd.User,
          "Group": wd.Group,
          "StartTime": formatDateTime(wd.startTime).date + " " + formatDateTime(wd.startTime).time,
          "EndTime": formatDateTime(wd.endTime).date + " " + formatDateTime(wd.endTime).time

          /* "Locazione Picking": wd.Storage_location,
          "Quantità Presa": wd.picked_qty,
          "Quantità Richiesta": wd.Source_target_qty,
          "Codice Prodotto": wd.Material,
          "Delivery": wd.Dest_Storage_Bin, */
        });
      });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    XLSX.writeFile(wb, `${waveId}.xlsx`);

  };


  return (
    <div className="position-relative">
      <div className="waves-container">
        
        <div className="mt-5">
          <div className="entry-heading pt-3">
            <img className="red-lines-img-l-waves" src="/images/logo-s.png" alt='' />
            <h1 className="  fw-bold d-flex align-items-center" style={{ color: '#0E3194' }}>
              Dettaglio Wave
              {/* <span className="red-lines-img-l-waves"> Dettaglio </span>&nbsp;Wave */}
            </h1>
            <h3 style={{ color: '#3A4472' }}>Stato Evasione Wave</h3>
          </div>
        </div>

        {downloadPDF ? (
          <>
            <WaveQRCode
              key="printComponent"
              waveId={downloadPDF}
              successCallback={() => {
                setDownloadPDF(null);
              }}
            />
          </>
        ) : null}

        <div className="h-100 w-100 position-relative">
          <div className="row">
            <div className="col d-flex justify-content-center">
              <div className="d-flex align-items-stretch" style={{ gap: '0.5rem', width: '100%' }}>
                
                {/* Delete Icon */}
                <div className="col-1 border border-gray7 rounded-start text-center d-flex align-items-center justify-content-center" style={{ marginRight: '0.5rem', minWidth: '80px' }}>
                  <img
                    className="border-0 m-1"
                    src="/images/delete-icon.svg"
                    alt="Elimina multiple waves"
                    style={{ width: '3.5rem', cursor: 'pointer' }}
                    onClick={() => {
                      handleMultipleWaveDelete();
                    }}
                  />
                </div>
                
                {/* Search/Filter Bar */}
                <div className="col-11 d-flex py-4 border border-gray7 rounded-end" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex align-items-center w-100 justify-content-center" style={{ gap: '1.5rem', padding: '0 2rem' }}>
                    
                    {/* Status Filter */}
                    <div className="waveStatusFilter">
                      <select
                        className="form-select rounded-pill border-2 p-2 d-inline border border-blue5 text-purple"
                        aria-label="Default select example"
                        value={searchByStatus}
                        onChange={e => {
                          handleStatusChange(e.target.value);
                        }}
                      >
                        <option value="">Tutte</option>
                        {activeTab === 'open' && (
                          <>
                            <option value="default">Nuove</option>
                            <option value="progress">In Lavorazione</option>
                          </>
                        )}
                        {activeTab === 'closed' && (
                          <>
                            <option value="completed">Completate</option>
                            <option value="anomaly">Chiuse Con Anomalie</option>
                          </>
                        )}
                      </select>
                    </div>
                    
                    {/* Wave Number Input */}
                    <div className="waveNumberSearchbar flex-grow-1">
                      <input
                        type="text"
                        className="form-control rounded-pill border-2 p-2 d-inline border border-blue5 text-purple"
                        aria-describedby="emailHelp"
                        placeholder="Wave Number"
                        value={searchByWaveId}
                        onChange={e => {
                          handleWaveIdChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSearchSubmit();
                          }
                        }}
                      />
                    </div>
                    
                    {/* Search Button */}
                    <div className="waveSearchButton">
                      <button
                        className="btn btn-blue rounded-pill d-inline w-100 h-100"
                        onClick={() => {
                          handleSearchSubmit();
                        }}
                      >
                        CERCA
                      </button>
                    </div>
                    
                    {/* Reset Button */}
                    {(searchByStatus || searchByWaveId) && (
                      <div className="waveResetButton ms-2">
                        <button
                          className="btn btn-outline-secondary rounded-pill d-inline px-3"
                          onClick={() => {
                            setSearchByStatus('');
                            setSearchByWaveId('');
                          }}
                          title="Pulisci filtri"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='deleteAll' style={{
          display: "flex",
          marginLeft: "2%",
          marginTop: "30px",
          gap: "40px",
          color: "red",
          alignItems: 'right',
          flexWrap: 'nowrap',
          width: '100%',
        }}>
          <div style={{ minWidth: 100, display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
            <input
              className="form-check-input border-red custom-delete-input"
              type="checkbox"
              onChange={e => {
                if (e.target.checked)
                  setAWaveDeleteIds([...waves.map(w => w.waveid)])
                else
                  setAWaveDeleteIds([])
              }}
              checked={aWaveDeleteIds.length > 0 && aWaveDeleteIds.length === waves.length}
            />
            <span style={{ marginLeft: 30 }}>Seleziona Tutte</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <button
              className={`btn rounded-pill mx-2 ${activeTab === 'open' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setActiveTab('open');
              }}
              style={{ minWidth: 160 }}
            >
              Da completare
            </button>
            <button
              className={`btn rounded-pill mx-2 ${activeTab === 'closed' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                setActiveTab('closed');
              }}
              style={{ minWidth: 160 }}
            >
              Completate
            </button>
          </div>
        </div>

        {/* Waves List */}
        <div className="accordion mb-5 mt-4" id="accordionPanelsStayOpenExample">
          {/* Progress indicator per caricamento dettagli in background */}
          {loadingProgress.total > 0 && (
            <div className="mb-3 text-center">
              <div className="alert alert-info d-flex align-items-center justify-content-center" style={{ padding: '8px 16px', fontSize: '14px' }}>
                <div className="spinner-border spinner-border-sm me-2" role="status" style={{ width: '16px', height: '16px' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                Caricamento dettagli in background... {loadingProgress.current}/{loadingProgress.total}
              </div>
            </div>
          )}
          
          {loading ? (
            <div className="mt-4 text-center">
              <div className="d-flex flex-column align-items-center">
                <div className="loading-circle-container mb-3" style={{ position: 'relative', width: '120px', height: '120px' }}>
                  {/* Cerchio di sfondo */}
                  <svg width="120" height="120" style={{ position: 'absolute', top: 0, left: 0 }}>
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#e9ecef"
                      strokeWidth="8"
                    />
                    {/* Cerchio di progresso */}
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#0E3194"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 50}`}
                      strokeDashoffset={`${2 * Math.PI * 50 * (1 - (loadingProgress.total > 0 ? loadingProgress.current / loadingProgress.total : 0))}`}
                      style={{ 
                        transition: 'stroke-dashoffset 0.3s ease',
                        transform: 'rotate(-90deg)',
                        transformOrigin: '60px 60px'
                      }}
                    />
                  </svg>
                  {/* Testo centrale */}
                  <div style={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0E3194' }}>
                      {loadingProgress.total > 0 ? Math.round((loadingProgress.current / loadingProgress.total) * 100) : 0}%
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                      {loadingProgress.current}/{loadingProgress.total}
                    </div>
                  </div>
                </div>
                <h4 className="text-gray8">Caricamento waves...</h4>
              </div>
            </div>
          ) : waves === null || waves.length === 0 ? (
            <h4 className="mt-4 text-center text-gray8">Nessun dato trovato.</h4>
          ) : (
            waves.map((row, index) => {
              return (
                <div className="accordion-item border-0" key={`waves-list-${row.waveid}`}>
                  <h2 className="accordion-header" id="panelsStayOpen-headingOne">

                    <div className="row justify-content-center align-items-center py-3">
                      
                      {/* Delete Check */}
                      <div className="col-1 align-items-center justify-content-center d-flex" style={{ width: '5%' }}>
                        <input
                          className="form-check-input mt-0 align-items-center form-check-input mt-0 align-items-center border-red custom-delete-input"
                          type="checkbox"
                          onChange={e => {
                            handleWaveDeleteCheck(row.waveid, e.target.checked);
                          }}
                          style={{ marginRight: '-1rem' }}
                          checked={aWaveDeleteIds.includes(row.waveid)}
                        />
                      </div>
                      {/* Images */}
                      <div className="col-2 d-flex align-items-center" style={{ minWidth: 120 }}>
                        <img className="border-0 imgWave" src={`/images/user-icon-${row.status}.svg`} alt="" />
                        <img
                          className="border-0 imgWave"
                          src="/images/qrcode.svg"
                          alt=""
                          style={{ cursor: 'pointer', marginLeft: 8 }}
                          onClick={() => {
                            setDownloadPDF(row.waveid);
                          }}
                        />
                      </div>

                      {/* Timestamp info - colonna separata */}
                      <div className="col-2 d-flex align-items-center" style={{ minWidth: 120 }}>
                        {(() => {
                          
                          const startDate = formatDateTime(row.startTime).date;
                          const starTime = formatDateTime(row.startTime).time;
                          const endDate = formatDateTime(row.endTime).date;
                          const endTime = formatDateTime(row.endTime).time;
                          
                          if (row.status === 'default') {
                            return null;
                          }
                          if (row.status === 'progress') {
                            if (startDate && startDate !== '-') {
                              return (
                                <div style={{ fontWeight: 'normal', fontSize: '1rem', width: '100%', display: 'block' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span style={{ textAlign: 'left', flex: 1 }}>Inizio:</span>
                                    <span style={{ textAlign: 'right', flex: 1 }}>{startDate} {starTime}</span>
                                  </div>
                                </div>
                              );
                            } else {
                              return null;
                            }
                          }
                          if (row.status === 'completed' || row.status === 'anomaly') {
                            if ((startDate && startDate !== '-') || (endDate && endDate !== '-')) {
                              return (
                                <div style={{ fontWeight: 'normal', fontSize: '1rem', width: '100%', display: 'block' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span style={{ textAlign: 'left', flex: 1 }}>Inizio:</span>
                                    <span style={{ textAlign: 'right', flex: 1 }}>{startDate} {starTime}</span>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <span style={{ textAlign: 'left', flex: 1 }}>Fine:</span>
                                    <span style={{ textAlign: 'right', flex: 1 }}>{endDate} {endTime}</span>
                                  </div>
                                </div>
                              );
                            } else {
                              return null;
                            }
                          }

                          return null;
                        })()}
                      </div>

                      {/* Button Wawe ID */}
                      <div className="col waveId" style={{ width: '45%' }}>
                        <span
                          className="btn fw-bold py-4 rounded-pill bg-navy w-100 text-white position-relative"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#panelsStayOpen-collapse-${index}`}
                          aria-expanded="false"
                          aria-controls={`panelsStayOpen-collapse-${index}`}
                          onClick={() => {
                            setVisibleWaveId(row.waveid);
                            getWaveInfo(row.waveid);
                          }}
                          style={{ textAlign: 'center' }}
                        >
                          <span style={{ display: 'block', width: '100%', textAlign: 'center', position: 'absolute', left: 0, right: 0, margin: '0 auto', pointerEvents: 'none' }}>
                            {row.waveid}
                          </span>
                          {row.ordersCount !== undefined && (
                            <span style={{ position: 'absolute', right: 30, fontWeight: 'normal', fontSize: '1rem', top: '50%', transform: 'translateY(-50%)' }}>
                              {row.ordersCount} righe
                            </span>
                          )}
                          {/* Spazio per rendere cliccabile tutto il bottone */}
                          <span style={{ opacity: 0 }}>{row.waveid}</span>
                        </span>
                      </div>

                      {/* Percentuale */}
                      <div className="col-2 d-inline wavePercentage" style={{ width: '20%' }}>
                        <div className="btn fw-bold py-4 rounded-pill bg-navy text-white w-100 d-flex" style={{ cursor: "auto", justifyContent: "center" }}>
                          <img className="border-0 mx-2" src="/images/graph.svg" alt="" style={{ width: '2rem' }} /> {row.percentage ? row.percentage : 0}%
                        </div>

                      </div>


                    </div>
                  </h2>

                  <div id={`panelsStayOpen-collapse-${index}`} className="accordion-collapse collapse" aria-labelledby={`#panelsStayOpen-heading-${index}`}>
                    {visibleWaveId === row.waveid && (
                      <Fragment key={`wave-list-${index}`}>
                        <div className="accordion-body p-0 my-2">
                          <div className="card border-0 rounded-5" style={{ backgroundColor: '#cbe2f1' }}>
                            <div className="card-body" style={{ display: "flex", flexDirection: "column" }}>
                              <button className='export-excel'>
                                <img
                                  className=""
                                  src="/images/save-file.svg"
                                  alt="Export Excel"
                                  style={{ width: '2rem', cursor: 'pointer' }}
                                  onClick={() => exportWave(row.waveid)}
                                />
                              </button>

                              {/* Contenitore solo scroll verticale, NO orizzontale */}
                              <div style={{ width: '100%' }}>
                                <div style={{ height: '20rem', overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
                                  <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                                    {/* Header */}
                                    <div className="row border-3 my-2 d-flex wave-header" style={{gap: '0.5rem', whiteSpace: 'nowrap', width: '100%', display: 'flex', justifyContent: 'center'}}>
                                      <div className="text-center" style={columnStyle}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Transfer Order
                                        </div>
                                      </div>
                                      <div className="text-center" style={columnStyleFlex11}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Data di creazione
                                        </div>
                                      </div>
                                      <div className="text-center" style={columnStyle}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Locazione Picking
                                        </div>
                                      </div>
                                      <div className="text-center" style={columnStyle}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Scaffale
                                        </div>
                                      </div>
                                      <div className="text-center" style={columnStyle}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Quantità Presa
                                        </div>
                                      </div>
                                      <div className="text-center" style={columnStyle}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Quantità Richiesta
                                        </div>
                                      </div>
                                      <div className="text-center" style={columnStyleFlex12}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Codice Prodotto
                                        </div>
                                      </div>
                                      <div className="text-center" style={columnStyle}>
                                        <div className="px-2 py-3 rounded-pill btn-4" style={headerCellStyle}>
                                          Delivery
                                        </div>
                                      </div>
                                    </div>
                                    {/* Righe dati */}
                                    {visibleWaveId && waveData && waveData.length > 0 ?
                                      waveData.map((item, index) => (
                                        <div className="row border-bottom border-3 border-white d-flex wave-header" key={`${item.Transfer_Order_Number}-${index}`} style={{gap: '0.5rem', backgroundColor: getOrderColor(item.picked_qty, item.Source_target_qty, item.User), paddingBottom: "1rem !important", paddingTop: "1rem !important", whiteSpace: 'nowrap', width: '100%', display: 'flex', justifyContent: 'center'}}>
                                          <div className="text-center" style={columnStyle}>
                                            <span className="px-2 py-2 w-100">{item.Transfer_Order_Number}</span>
                                          </div>
                                          <div className="text-center" style={columnStyleFlex11}>
                                            <span className="px-2 py-2 w-100">{item.Creation_Date ? item.Creation_Date.substring(0, 10) : ''}</span>
                                          </div>
                                          <div className="text-center" style={columnStyle}>
                                            <span className="px-2 py-2 w-100">{item.Storage_location}</span>
                                          </div>
                                          <div className="text-center" style={columnStyle}>
                                            <span className="px-2 py-2 w-100">{item.Source_Storage_Bin}</span>
                                          </div>
                                          <div className="text-center" style={columnStyle}>
                                            <span className="px-2 py-2 w-100">{item.picked_qty}</span>
                                          </div>
                                          <div className="text-center" style={columnStyle}>
                                            <span className="px-2 py-2 w-100">{item.Source_target_qty}</span>
                                          </div>
                                          <div className="text-center" style={columnStyleFlex12}>
                                            <span className="px-2 py-2 w-100">{item.Material}</span>
                                          </div>
                                          <div className="text-center" style={columnStyle}>
                                            <span className="px-2 py-2 w-100">{item.Dest_Storage_Bin}</span>
                                          </div>
                                        </div>
                                      )) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {showDeleteModal && (
            <DeleteWaveModal
              aWaveDeleteIds={aWaveDeleteIds}
              waveType={waveType}
              _onSuccess={() => {
                setAWaveDeleteIds([]);
                setShowDeleteModal(false);
                getWaves();
              }}
              _onCancel={() => {
                setShowDeleteModal(false);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Waves;

// Funzione per formattare timestamp unix a gg-mm-aaaa hh:mm (gestione secondi/millisecondi)
function formatDateTime(unix) {

  if (!unix || unix === '-' || isNaN(unix)) {

    return { date: '-', time: '-' };
  }
  let ts = Number(unix);
  if (ts < 1e12) ts = ts * 1000; // se è in secondi, converto in ms
  const d = new Date(ts);
  if (isNaN(d.getTime())) {

    return { date: '-', time: '-' };
  }
  const pad = n => n < 10 ? '0' + n : n;
  const result = {
    date: `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`
  };

  return result;
}

// Definizione degli stili riutilizzabili per header e colonne dati
const columnStyle = { flex: 1, minWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const columnStyleFlex11 = { ...columnStyle, flex: 1.1 };
const columnStyleFlex12 = { ...columnStyle, flex: 1.2 };
const headerCellStyle = { pointerEvents: 'none', userSelect: 'none', cursor: 'default', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };
